import React, { useState, useEffect } from 'react'
import { ShieldCheck, Award, Heart, Sparkles, Users } from 'lucide-react'

export default function About() {
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/ansatte')
        if (res.ok) {
          const data = await res.json()
          setTeam(data)
        }
      } catch (error) {
        console.error('Fejl ved hentning af ansatte:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTeam()
  }, [])

  const values = [
    {
      icon: <i className="fa-duotone fa-solid fa-paint-roller text-2xl text-sky-500" />,
      title: 'Højeste Faglighed',
      description: 'Vi er uddannede malere med mange års erfaring. Vi kender de rigtige teknikker og materialer til enhver opgave.'
    },
    {
      icon: <i className="fa-duotone fa-solid fa-shield-check text-2xl text-sky-500" />,
      title: 'Kvalitetsgaranti',
      description: 'Vi sikrer dig altid et professionelt resultat. Vi forlader ikke opgaven, før du er 100% tilfreds med det udførte arbejde.'
    },
    {
      icon: <i className="fa-duotone fa-solid fa-broom text-2xl text-sky-500" />,
      title: 'Rent & Pænt Arbejde',
      description: 'Vi dækker altid grundigt af før start og rydder pænt op efter os hver dag. Et rent arbejdsmiljø giver det bedste resultat.'
    },
    {
      icon: <i className="fa-duotone fa-solid fa-leaf text-2xl text-sky-500" />,
      title: 'Miljø & Indeklima',
      description: 'Vi anvender svanemærkede og lugtsvage kvalitetsprodukter, der skåner både miljøet og sikrer et sundt indeklima v din bolig.'
    }
  ]

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Header */}
      <section className="bg-white pt-16 pb-8 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-4 text-center space-y-3">
          <h1 className="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-900">Om Os</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Lær Probyg Maler bedre at kende. Vi er et professionelt malerfirma med fokus på kvalitet, pålidelighed og glade kunder.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
            {/* Left: Image */}
            <div className="aspect-video w-full overflow-hidden rounded-2xl shadow-lg border border-slate-200 lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/197fd4aa-3a29-4132-bfc8-c164f43f9a90.jpg"
                alt="Probyg Maler kontor og arbejde"
                className="h-full w-full object-cover object-center"
              />
            </div>

            {/* Right: Text */}
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-black">Vores Historie</span>
              <h2 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
                Erfaring, kvalitet og personlig service
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Probyg Maler blev stiftet med en klar mission: At hæve barren for, hvad kunder kan forvente af et malerfirma. Vi har gennem årene opbygget en solid kundekreds af både private boligejere, virksomheder og boligforeninger på hele Sjælland.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Vi tror på, at et godt stykke arbejde starter med en god dialog. Derfor tager vi os altid tid til at lytte til dine ønsker, rådgive om de bedste løsninger og levere et skriftligt tilbud, der holder.
              </p>
              <p className="text-slate-600 leading-relaxed font-semibold text-slate-900">
                Uanset om du skal have malet en enkelt væg eller renoveret en hel ejendom, går vi til opgaven med samme faglige stolthed og engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <h2 className="text-xs font-bold uppercase tracking-widest text-black">Vores Værdier</h2>
            <p className="text-3xl font-semibold text-slate-900 sm:text-4xl">
              Det fundament, vi bygger på
            </p>
            <p className="text-lg text-slate-600">
              Vores værdier er ikke bare ord på et papir. De styrer vores daglige arbejde, vores møde med kunderne og den kvalitet, vi leverer.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value, idx) => (
              <div key={idx} className="rounded-2xl bg-white p-6 border border-slate-150 shadow-sm space-y-4">
                <div className="w-12 h-12 flex items-center justify-start">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{value.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-900">
              <Users className="h-4 w-4" />
              Mød holdet
            </div>
            <p className="text-3xl font-semibold text-slate-900 sm:text-4xl">
              Dine professionelle malere
            </p>
            <p className="text-lg text-slate-600">
              Vores team består af engagerede og uddannede malere, der alle brænder for det gode håndværk og den gode kundeservice.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              <div className="flex justify-center py-12 col-span-full">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-black border-t-transparent" />
              </div>
            ) : team.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 max-w-md mx-auto col-span-full">
                <p className="text-sm text-slate-600">Ingen ansatte fundet.</p>
              </div>
            ) : (
              team.map((member, idx) => (
                <div key={member.id || idx} className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
                  <div className="aspect-[3/4] w-full overflow-hidden bg-slate-200">
                    <img
                      src={member.billede?.[0]}
                      alt={member.navn}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{member.navn}</h3>
                      <p className="text-xs font-semibold text-slate-900 uppercase tracking-wider">{member.stilling}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
